import { useCallback, useEffect, useState } from 'react';
import ValidatedInput from '../custom-input/ValidatedInput';
import FilterDropdown, { FilterOption } from '../custom-input/FilterDropdown';
import MultiSelectDropdown from '../custom-input/multiSelectDropdown';
import { AnimatedDots } from '../loader/AnimatedDots';
import TrashCan from '../svgs/TrashCan.svg';
import { getStorageCanisterByTank, getStorageSites, getStorageTanksBySite } from '../../api/storageItemsApi';
import {
  getAccountStorageCanisterByTank,
  getAccountStorageSites,
  getAccountStorageTanksBySite,
} from '../../api/accountStorageItemsApi';
import { showToast } from '../../services/toast.service';
import { StorageTankOptions } from '../../types/enums/storageTank.enum';
import { AccountStorageCanister, AccountStorageSite, AccountStorageTank, PagedResponse } from '../../types/interfaces';
import { selectOption } from '../../types/interfaces/selectOption.interface';
import { StorageCanister, StorageSite, StorageTank } from '../../types/interfaces/storage.interfaces';
import { LABEL_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';

interface StorageLocationProps {
  index: number;
  removeStorageLocation: (index: number) => void;
  handleSiteChange: (e: FilterOption<any>, index: number) => void;
  handleTankChange: (e: FilterOption<any>, index: number) => void;
  handleCanisterChange: (e: FilterOption<any>, index: number) => void;
  handleQuantityChange: (e: string, index: number) => void;
  handleStrawCodesChange: (e: string, index: number) => void;
  selectedStorageLocations: any;
  storageTankOptions: StorageTankOptions;
  activeLocationsOnly?: boolean;
  strawCodeOptions?: FilterOption<string>[];
  itemType: string;
  readOnlyLocationFields?: boolean;
  hideRemoveLocation?: boolean;
  useAccountStorage?: boolean;
}

const normalizeList = <T,>(data: T[] | PagedResponse<T>): T[] => {
  if (Array.isArray(data)) return data;
  return data?.result ?? [];
};

const StorageLocationRow = ({
  index,
  storageTankOptions,
  selectedStorageLocations,
  removeStorageLocation,
  handleSiteChange,
  handleTankChange,
  handleCanisterChange,
  handleQuantityChange,
  handleStrawCodesChange,
  activeLocationsOnly = false,
  strawCodeOptions,
  itemType,
  readOnlyLocationFields = false,
  hideRemoveLocation = false,
  useAccountStorage = false,
}: StorageLocationProps) => {
  const initialState = { value: '', name: '' };
  const [storageSites, setStorageSites] = useState<selectOption[]>([initialState]);
  const [storageTanks, setStorageTanks] = useState<selectOption[]>([initialState]);
  const [storageCanisters, setStorageCanisters] = useState<selectOption[]>([initialState]);
  const [isLoading, setIsLoading] = useState({ sites: true, tanks: false, canisters: false });

  const getSites = useCallback(async () => {
    try {
      if (useAccountStorage) {
        const res = await getAccountStorageSites({ filter: activeLocationsOnly ? 'Active eq true' : undefined });
        const sites = normalizeList(res.data as AccountStorageSite[] | PagedResponse<AccountStorageSite>).map(
          (s: AccountStorageSite) => ({
            value: String(s.accountStorageSiteId),
            name: s.name + (s.active ? '' : ' - (Inactive)'),
          }),
        );
        setStorageSites(sites);
      } else {
        const res = await getStorageSites({ filter: activeLocationsOnly ? 'Active eq true' : undefined });
        const storageSites = (res.data as StorageSite[]).map((s: StorageSite) => ({
          value: String(s.storageSiteId),
          name: s.name + (s.active ? '' : ' - (Inactive)'),
        }));
        setStorageSites(storageSites);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(prevIsLoading => ({ ...prevIsLoading, sites: false }));
    }
  }, [activeLocationsOnly, useAccountStorage]);

  useEffect(() => {
    getSites();
  }, [getSites]);

  const getTanks = async (siteId: string) => {
    setIsLoading(prevIsLoading => ({ ...prevIsLoading, tanks: true }));

    try {
      if (useAccountStorage) {
        const res = await getAccountStorageTanksBySite(siteId, storageTankOptions, {
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
        });
        const tanks = normalizeList(res.data as AccountStorageTank[] | PagedResponse<AccountStorageTank>).map(
          (s: AccountStorageTank) => ({
            value: String(s.accountStorageTankId),
            name: s.name + (s.active ? '' : ' - (Inactive)'),
          }),
        );
        setStorageTanks(tanks);
      } else {
        const res = await getStorageTanksBySite(siteId, storageTankOptions, {
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
        });
        const tanks = res.data.map((s: StorageTank) => ({
          value: String(s.storageTankId),
          name: s.name + (s.active ? '' : ' - (Inactive)'),
        }));
        setStorageTanks(tanks);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(prevIsLoading => ({ ...prevIsLoading, tanks: false }));
    }
  };

  const getValidStrawCodes = (): string[] => {
    if (!strawCodeOptions || strawCodeOptions.length === 0) return [];
    const selectedCodes: string[] = (selectedStorageLocations[index].strawCodesText ?? '').split(',').filter(Boolean);
    const availableCodes: string[] = strawCodeOptions.map(opt => opt.value);
    const validCodes: string[] = selectedCodes.filter(code => availableCodes.includes(code));
    if (validCodes.join(',') !== selectedCodes.join(',')) {
      handleStrawCodesChange(validCodes.join(','), index);
    }
    return validCodes;
  };

  const validStrawCodes = getValidStrawCodes();

  const getCanisters = async (tankId: string) => {
    setIsLoading(prevIsLoading => ({ ...prevIsLoading, canisters: true }));

    try {
      if (useAccountStorage) {
        const res = await getAccountStorageCanisterByTank(tankId, {
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
        });
        const canisters = normalizeList(res.data as AccountStorageCanister[] | PagedResponse<AccountStorageCanister>).map(
          (s: AccountStorageCanister) => ({
            value: String(s.accountStorageCanisterId),
            name: s.name + (s.active ? '' : ' - (Inactive)'),
          }),
        );
        setStorageCanisters(canisters);
      } else {
        const res = await getStorageCanisterByTank(tankId, { filter: activeLocationsOnly ? 'Active eq true' : undefined });
        const canisters = (res.data as StorageCanister[]).map((s: StorageCanister) => ({
          value: String(s.storageCanisterId),
          name: s.name + (s.active ? '' : ' - (Inactive)'),
        }));
        setStorageCanisters(canisters);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(prevIsLoading => ({ ...prevIsLoading, canisters: false }));
    }
  };

  const isLoadingPlaceHolder = (isLoadingOptions: boolean, filterDropdownName: string) => {
    return isLoadingOptions ? (
      <span>
        Loading {filterDropdownName} <AnimatedDots />
      </span>
    ) : (
      `Select ${filterDropdownName}`
    );
  };

  const handleRemoveStrawCode = (codeToRemove: string) => {
    const currentCodes = (selectedStorageLocations[index].strawCodesText ?? '').split(',').filter(Boolean);

    const updatedCodes = currentCodes.filter((code: string) => code !== codeToRemove);

    handleStrawCodesChange(updatedCodes.join(','), index);
  };

  return (
    <div>
      <div className="select-storage-item">
        {!hideRemoveLocation && selectedStorageLocations.length > 1 && (
          <TrashCan onClick={() => removeStorageLocation(index)} className="trash-can" />
        )}
        <div className="select-storage-item-grid">
          <div className="select-column">
            <label>{LABEL_CONSTANTS.SITE}: </label>
            <FilterDropdown
              placeholder={isLoadingPlaceHolder(isLoading.sites, 'Site')}
              options={storageSites}
              validate={false}
              value={selectedStorageLocations[index].site}
              onChange={e => {
                getTanks(e.value);
                handleSiteChange(e, index);
              }}
              disabled={isLoading.sites || readOnlyLocationFields}
            />
          </div>
          <div className="select-column">
            <label>{LABEL_CONSTANTS.TANK}: </label>
            <FilterDropdown
              placeholder={isLoadingPlaceHolder(isLoading.tanks, 'Tank')}
              options={storageTanks}
              validate={false}
              value={selectedStorageLocations[index].tank}
              onChange={e => {
                getCanisters(e.value);
                handleTankChange(e, index);
              }}
              disabled={!selectedStorageLocations[index].site.value || isLoading.tanks || readOnlyLocationFields}
            />
          </div>
          <div className="select-column">
            <label>{LABEL_CONSTANTS.CANISTER}: </label>
            <FilterDropdown
              placeholder={isLoadingPlaceHolder(isLoading.canisters, 'Canister')}
              options={storageCanisters.filter(sc => !selectedStorageLocations.some((s: any) => s.canister.value === sc.value))}
              validate={false}
              value={selectedStorageLocations[index].canister}
              onChange={e => handleCanisterChange(e, index)}
              disabled={
                !selectedStorageLocations[index].site.value ||
                !selectedStorageLocations[index].tank.value ||
                isLoading.canisters ||
                readOnlyLocationFields
              }
            />
          </div>
          <div className="select-column">
            <label>{LABEL_CONSTANTS.QUANTITY}: </label>
            <ValidatedInput
              setValidatedState={e => handleQuantityChange(e.value, index)}
              placeholder={'Enter Quantity'}
              label={''}
              validators={[]}
              type="number"
              disabled={readOnlyLocationFields}
              validatedStateForAutoFill={{
                value: (selectedStorageLocations[index].quantity.value ?? '').toString(),
                valid: true,
              }}
            />
          </div>

          {itemType === 'Embryo' && (
            <div className="multiselect-column">
              <label>{LABEL_CONSTANTS.STRAW_LOT}: </label>
              {(() => {
                const selectedInOtherRows = selectedStorageLocations
                  .filter((_: any, i: number) => i !== index)
                  .flatMap((loc: any) => (loc.strawCodesText ?? '').split(',').filter(Boolean));

                const availableStrawOptions =
                  strawCodeOptions && strawCodeOptions.length > 0
                    ? strawCodeOptions.filter(opt => !!opt.value).filter(opt => !selectedInOtherRows.includes(opt.value))
                    : [{ name: 'No straws found', value: '__no_straws__', optionDisabled: true }];

                return (
                  <MultiSelectDropdown
                    placeholder="Select Straw IDs"
                    options={availableStrawOptions}
                    value={availableStrawOptions.filter(opt =>
                      (selectedStorageLocations[index].strawCodesText ?? '').split(',').includes(opt.value),
                    )}
                    onChange={selected => {
                      const validSelected = selected.filter(o => o.value !== '__no_straws__');
                      const joined = validSelected.map(o => o.value).join(',');
                      handleStrawCodesChange(joined, index);
                    }}
                  />
                );
              })()}
            </div>
          )}

          {validStrawCodes.length > 0 && (
            <div className="straw-codes-summary">
              <label>{LABEL_CONSTANTS.SELECTED_STRAW_LOTS}: </label>
              <div className="straw-capsule-container">
                {selectedStorageLocations[index].strawCodesText
                  .split(',')
                  .filter(Boolean)
                  .map((code: string) => (
                    <span key={code} className="straw-chip">
                      {code}
                      <button className="remove-chip" onClick={() => handleRemoveStrawCode(code)} type="button">
                        ✕
                      </button>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageLocationRow;
